import { hasItems } from "common/arrays";
import { strDateToYearMonthDay } from "common/dates";

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

export const getYear = (date) => {
  const out =
    typeof date === "string" && DATE_ONLY_RE.test(date)
      ? strDateToYearMonthDay(date)?.year ?? null
      : null;

  return typeof out === "number" && isFinite(out) ? out : null;
};

export const getCurrentYear = () => new Date().getFullYear();

export const getYearRange = ({ endDate, numberOfYears }) => {
  const useDefault =
    numberOfYears == null || isNaN(numberOfYears) || numberOfYears <= 0;
  numberOfYears = useDefault ? 10 : numberOfYears;
  const currentYear = (getYear(endDate) ?? getCurrentYear()) - 1;
  const startYear = currentYear - numberOfYears + 1;
  return Array.from({ length: numberOfYears }, (_, i) => startYear + i);
};

export const coverageLabel = (lossCategories, key) => {
  const label = (lossCategories ?? []).find((line) => line.key === key)
    ?.display;
  return label ?? key ?? "";
};

export const formatDate = (date, year) => {
  const dateParts =
    typeof date === "string" && DATE_ONLY_RE.test(date)
      ? strDateToYearMonthDay(date)
      : null;
  if (dateParts == null || !Number.isFinite(year)) {
    return "";
  }
  const monthDay = `${dateParts.month}/${dateParts.day}`;
  const enDashChar = "\u2013";
  return `${monthDay}/${year % 100}${enDashChar}${(year + 1) % 100}`;
};

export const fillMissingYears = (
  data,
  lossCategories,
  { endDate, numberOfYears } = {}
) => {
  const years = getYearRange({ endDate, numberOfYears });

  const result = [];

  for (const [coverage, entries] of Object.entries(data ?? {})) {
    const data = [];
    for (let year of years) {
      const yearData = entries.find((entry) => entry.year === year) ?? {};
      data.push({
        year: year,
        paid: yearData.paid ?? 0,
        reserved: yearData.reserved ?? 0,
        incurred: yearData.incurred ?? 0,
        count: yearData.count ?? 0,
      });
    }

    if (data.some((x) => x.count > 0)) {
      result.push({
        coverage,
        label: coverageLabel(lossCategories, coverage),
        data,
      });
    }
  }

  return result;
};

export const getHiddenYears = (hiddenYears, coverage) =>
  hiddenYears?.[coverage] ?? [];

export const hideYear = (hiddenYears, coverage, year) => {
  const existing = getHiddenYears(hiddenYears, coverage);
  if (existing.includes(year)) {
    return hiddenYears ?? {};
  }
  return { ...(hiddenYears ?? {}), [coverage]: [...existing, year] };
};

export const restoreYear = (hiddenYears, coverage, year) => {
  const updated = getHiddenYears(hiddenYears, coverage).filter(
    (y) => y !== year
  );
  const next = { ...(hiddenYears ?? {}) };
  if (updated.length > 0) {
    next[coverage] = updated;
  } else {
    delete next[coverage];
  }
  return next;
};

export const applyCoverages = (activeCoverages, currentData, { endDate }) => {
  const result = [];

  if (!hasItems(activeCoverages) || !Array.isArray(currentData)) {
    return result;
  }

  activeCoverages.forEach((coverage) => {
    const existing = currentData.filter((x) => x.coverage === coverage)?.[0];

    if (existing == null) {
      const years = getYearRange({ numberOfYears: 10, endDate });
      years.sort((a, b) => b - a);
      const data = years.map((year) => ({
        year,
        paid: 0,
        reserved: 0,
        incurred: 0,
        count: 0,
      }));
      result.push({ coverage, data });
    } else {
      result.push({ coverage, data: existing.data });
    }
  });

  return result;
};

export const addRow = (coverage, currentData) => {
  return currentData.map((item) => {
    if (item.coverage === coverage) {
      const years = item.data.map((r) => r.year);
      const newYear =
        years.length > 0 ? Math.min(...years) - 1 : getCurrentYear() - 1;
      const newRow = {
        year: newYear,
        paid: 0,
        reserved: 0,
        incurred: 0,
        count: 0,
      };
      return { coverage: item.coverage, data: [...item.data, newRow] };
    }
    return item;
  });
};

export const deleteRow = (coverage, currentData, year) => {
  return currentData.map((item) => {
    if (item.coverage === coverage) {
      return {
        coverage: item.coverage,
        data: item.data.filter((row) => row.year !== year),
      };
    }
    return item;
  });
};

export const applyRowUpdate = (coverage, currentData, updatedRow) => {
  return currentData.map((item) => {
    if (item.coverage === coverage) {
      return {
        coverage: item.coverage,
        data: item.data.map((row) =>
          row.year === updatedRow.year ? updatedRow : row
        ),
      };
    } else {
      return item;
    }
  });
};

export const toCsvString = (currentData) => {
  if (currentData == null) return "";

  const headings = [
    "Coverage",
    "Year",
    "No of Claims",
    "Total Paid",
    "Total Reserved",
    "Total Incurred",
  ];

  const lines = [headings];

  currentData.forEach((item) => {
    item.data.forEach((x) => {
      lines.push([
        item.coverage,
        x.year,
        x.count,
        x.paid,
        x.reserved,
        x.incurred,
      ]);
    });
  });

  return lines.map((line) => line.join(",")).join("\n");
};
